import React, { useState, useEffect } from 'react';
import { Occupation, OccupationExperience } from '../types';

interface OccupationSelectorProps {
  experiences: OccupationExperience[];
  onChange: (experiences: OccupationExperience[]) => void;
}

const OccupationSelector: React.FC<OccupationSelectorProps> = ({ experiences, onChange }) => {
  const [occupations, setOccupations] = useState<Occupation[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtered, setFiltered] = useState<Occupation[]>([]);

  useEffect(() => {
    fetchOccupations();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      setFiltered(
        occupations.filter(o => o.name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    } else {
      // デフォルトで候補を表示（先頭10件）
      setFiltered(occupations.slice(0, 10));
    }
  }, [searchTerm, occupations]);

  const fetchOccupations = async () => {
    try {
      const response = await fetch('/api/v1/occupations');
      const data = await response.json();
      setOccupations(data);
    } catch (error) {
      console.error('職種取得エラー:', error);
    }
  };

  const addOccupation = (occupation: Occupation) => {
    if (!experiences.find(exp => exp.occupation_id === occupation.id)) {
      onChange([...experiences, { occupation_id: occupation.id, years_of_experience: 1 }]);
    }
    setSearchTerm('');
  };

  const removeOccupation = (occupationId: number) => {
    onChange(experiences.filter(exp => exp.occupation_id !== occupationId));
  };

  const updateYears = (occupationId: number, years: number) => {
    onChange(experiences.map(exp =>
      exp.occupation_id === occupationId ? { ...exp, years_of_experience: years } : exp
    ));
  };

  const getOccupationName = (occupationId: number) => {
    return occupations.find(o => o.id === occupationId)?.name || '';
  };

  return (
    <div>
      <div className="mb-3">
        <label className="form-label">経験のある職種を検索・追加</label>
        <input
          type="text"
          className="form-control"
          placeholder="職種名を入力..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
        />

        {filtered.length > 0 && (
          <div className="mt-2 border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {filtered.slice(0, 10).map(occupation => (
              <button
                key={occupation.id}
                type="button"
                className="btn btn-outline-primary btn-sm me-2 mb-2"
                onClick={() => addOccupation(occupation)}
              >
                {occupation.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="form-label">選択済み職種</label>
        {experiences.length === 0 ? (
          <p className="text-muted">職種が選択されていません</p>
        ) : (
          experiences.map(exp => (
            <div key={exp.occupation_id} className="d-flex align-items-center mb-2">
              <span className="me-3 flex-grow-1">{getOccupationName(exp.occupation_id)}</span>
              <input
                type="number"
                className="form-control me-2"
                style={{ width: '100px' }}
                min="0"
                max="50"
                value={exp.years_of_experience}
                onChange={(e) => updateYears(exp.occupation_id, parseInt(e.target.value) || 0)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
              />
              <span className="me-2">年</span>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => removeOccupation(exp.occupation_id)}
              >
                削除
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OccupationSelector;
